import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { formatCount } from '@/utils/number';
import { LATEST_START_BADGE_CLASS } from '../constants/moTravel.constants';
import { formatShortDay } from '../utils/dayLabels';
import { describeStartPlace, type StartPlace } from '../utils/startPlaces';
import { MoStartIcon } from './MoStartIcon';

interface StartDayCountProps {
  dates: string[];
}

function StartDayCount({ dates }: StartDayCountProps) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span className="min-w-4 cursor-help rounded-full bg-muted px-1 text-center text-3xs leading-4 font-semibold text-muted-foreground tabular-nums">
            {dates.length}
          </span>
        }
      />
      <TooltipContent className="flex-col items-start gap-1.5 py-2">
        <span className="font-semibold">{formatCount(dates.length, 'day')} started here</span>
        <span className="flex max-w-56 flex-wrap gap-1">
          {dates.map((date) => (
            <span
              key={date}
              className="rounded bg-background/15 px-1.5 py-0.5 text-2xs tabular-nums"
            >
              {formatShortDay(date)}
            </span>
          ))}
        </span>
      </TooltipContent>
    </Tooltip>
  );
}

interface MoStartPlacePickerProps {
  startPlaces: StartPlace[];
  onStartSelect: (startKey: string) => void;
  onStartHover: (startKey: string | null) => void;
  className?: string;
}

export function MoStartPlacePicker({
  startPlaces,
  onStartSelect,
  onStartHover,
  className,
}: MoStartPlacePickerProps) {
  const latestStartKey = startPlaces[0]?.startKey;

  return (
    <div
      role="group"
      aria-label="Start points"
      className={cn(
        'flex max-w-[calc(100%-4.5rem)] items-center gap-1 overflow-x-auto rounded-lg border bg-card/95 p-1 shadow-md backdrop-blur-sm [scrollbar-width:none]',
        className,
      )}
    >
      {startPlaces.map((startPlace) => {
        const isLatest = startPlace.startKey === latestStartKey;
        const startLabel = describeStartPlace(startPlace);
        return (
          <button
            key={startPlace.startKey}
            type="button"
            onClick={() => onStartSelect(startPlace.startKey)}
            onMouseEnter={() => onStartHover(startPlace.startKey)}
            onMouseLeave={() => onStartHover(null)}
            onFocus={() => onStartHover(startPlace.startKey)}
            onBlur={() => onStartHover(null)}
            aria-label={startLabel}
            className="flex h-8 shrink-0 items-center gap-1.5 rounded-md pr-1.5 pl-1 text-xs font-medium transition-colors hover:bg-muted/70 focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none"
          >
            <span className="flex size-6 shrink-0 items-center justify-center rounded bg-route-start/10 text-route-start">
              <MoStartIcon className="size-3.5" />
            </span>
            <span className="max-w-28 min-w-0 truncate" title={startPlace.title}>
              {startPlace.title}
            </span>
            {isLatest && <span className={LATEST_START_BADGE_CLASS}>Latest</span>}
            <StartDayCount dates={startPlace.dates} />
          </button>
        );
      })}
    </div>
  );
}
